<template>
  <div class="shipment-info">
    <el-collapse
        v-model="activeCollapseName">
      <el-collapse-item
          v-if="$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.SHIPMENT_INFO)"
          id="shipmentInfo"
          title="配送情報"
          name="shipmentInfo"
          class="shipment-info-child">
        <AttributeButton
            v-if="$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.ATTRIBUTE)"
            class="attribute-button"
            :site-code="siteCode"
            :editable="editableAttribute"
            :attribute-values="attributes"
            page-name="order_detail"
            area-name="ship_info" />
        <el-row>
          <el-col :span="12">
            <BaseTableDetail
                class="shipment-item"
                :table-set-list="shipmentInfo1"
                :display-item="shipment"
                :editable="editableShipment"
                :site-code="siteCode"
                :validate-scope="validateScope" />
          </el-col>
          <el-col :span="12">
            <BaseTableDetail
                class="shipment-item"
                :table-set-list="shipmentInfo2"
                :display-item="shipment"
                :editable="editableShipment"
                :site-code="siteCode"
                :validate-scope="validateScope" />
          </el-col>
        </el-row>
      </el-collapse-item>
    </el-collapse>
    <el-row v-if="showItemList">
      <el-col :span="24">
        <OrderDetailItems
            :order-detail="shipment"
            :origin-data="originData"
            :mode="mode"
            :type="type"
            title="商品明細"
            validate-scope="orderDetail"
            class="table orderdetail-items shipment-info-child" />
      </el-col>
    </el-row>
  </div>
</template>

<style scoped>
.shipment-info >>> .el-collapse-item {
  box-shadow: initial;
}
.attribute-button {
  padding-left: 15px;
}
.table.orderdetail-items {
  margin-top: 1px;
  box-shadow: initial;
}
.shipment-info-child >>> .el-collapse-item__header,
.shipment-info-child >>> .table-header {
  font-size: 15px;
  line-height: 44px;
  height: 44px;
  padding-left: 45px;
}

/* TODO ccs-ui.css がきいてしまうので暫定対応。いずれ削除したい */
.shipment-info {
  margin-top: -10px;
  margin-bottom: -10px;
}
.shipment-item >>> table tr,
.shipment-item >>> table tr:hover > td {
  background-color: initial;
}
.shipment-item >>> table th {
  border-bottom: initial;
}
.shipment-info >>> .table.orderdetail-items table th:first-child {
    padding-left: 15px;
    padding-right: 10px;
}
.shipment-info >>> .table.orderdetail-items table th:last-child {
    padding-left: 10px;
    padding-right: 15px;
}
.shipment-info >>> .table.orderdetail-items table:first-of-type > tr > td {
    padding: 0;
}
</style>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import * as define from '@/define'

import BaseTableDetail from '@/components/molecules/BaseTableDetail'
import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'

import AttributeButton from '@/components/organisms/AttributeButton'

import OrderDetailItems from '@/components/pages/order/OrderDetailItems'

const EDITABLE = Object.freeze({
  [define.ORDER_MODE.EDIT]: Object.freeze({
    name: true, nameKana: true, tel: true, address: true, giftType: true, giftMessage: true, deliveryDate: true, deliveryTime: true
  }),
  [define.ORDER_MODE.SHORTAGE]: Object.freeze({
    name: true, nameKana: true, tel: true, address: true, giftType: true, giftMessage: true, deliveryDate: true, deliveryTime: true
  }),
  [define.ORDER_MODE.EXCHANGE]: Object.freeze({
    name: true, nameKana: true, tel: true, address: true, giftType: true, giftMessage: true, deliveryDate: true, deliveryTime: true
  }),
  [define.ORDER_MODE.SHIPMENT]: Object.freeze({
    name: true, nameKana: true, tel: true, address: true, giftType: true, giftMessage: true, deliveryDate: true, deliveryTime: true
  })
})

export default {
  name: 'OrderDetailShipment',
  components: {
    BaseTableDetail,
    AttributeButton,
    OrderDetailItems
  },
  inject: ['parentValidator'],
  props: {
    shipment: {
      type: Object,
      default: () => ({})
    },
    originData: {
      type: Object,
      default: () => ({})
    },
    mode: {
      type: Number,
      default: define.ORDER_MODE.NORMAL
    },
    type: {
      type: String,
      default: 'normal',
      validator: function (value) {
        return ['normal', 'origin', 'total'].indexOf(value) !== -1
      }
    },
    validateScope: {
      type: String,
      default: null
    },
    expandShipmentInfo: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      activeCollapseName: []
    }
  },
  computed: {
    define: () => define,
    ...mapGetters('OrderDetail', {
      orderDetail: 'orderDetail',
      siteCode: 'siteCode'
    }),
    editable () {
      return EDITABLE[this.mode] || {}
    },
    editableShipment () {
      if (this.type === 'normal' && (this.mode !== define.ORDER_MODE.SHIPMENT || !this.shipment.id)) {
        return this.editable
      } else {
        return {}
      }
    },
    editableAttribute () {
      return !this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.Attribute.ATTRIBUTE_EDIT) &&
        this.type === 'normal' &&
        [define.ORDER_MODE.EDIT, define.ORDER_MODE.SHORTAGE, define.ORDER_MODE.EXCHANGE].includes(this.mode)
    },
    attributes () {
      return _.get(this.shipment, 'attributes', [])
    },
    showItemList () {
      if (!this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.ITEM_DETAIL)) {
        return false
      }

      return _.chain(this.shipment)
        .pick(['items', 'giftItems', 'shippingItems'])
        .find(val => val != null && val.length > 0)
        .value()
    },
    shipmentInfo1 () {
      return [
        {
          label: '配送番号',
          properties: [ {
            path: 'shipmentId', type: 'String',
            funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.NUMBER
          } ],
          horizontal: true
        },
        {
          label: '配送先氏名',
          properties: [
            {
              type: 'String', elementType: ELEMENT_TYPE.CELL,
              validateRule: `checkReceiverName:${this.emptyReceiverName}`,
              editableId: 'name', editableCondition: this.shipInfoEditableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.RECIPIENT_NAME,
              horizontal: true,
              properties: [
                {
                  path: 'receiver.lastName', type: 'String', elementType: ELEMENT_TYPE.INPUT
                }, {
                  path: 'receiver.firstName', type: 'String', elementType: ELEMENT_TYPE.INPUT
                }
              ]
            }
          ]
        },
        {
          label: '配送先氏名カナ',
          properties: [
            {
              type: 'String', elementType: ELEMENT_TYPE.CELL,
              editableId: 'nameKana', editableCondition: this.shipInfoEditableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.RECIPIENT_NAME_KANA,
              horizontal: true,
              properties: [
                {
                  path: 'receiver.lastNameKana', type: 'String', elementType: ELEMENT_TYPE.INPUT
                }, {
                  path: 'receiver.firstNameKana', type: 'String', elementType: ELEMENT_TYPE.INPUT
                }
              ]
            }
          ]
        },
        {
          label: '配送先TEL',
          properties: [
            {
              path: 'receiver.phone', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'tel', editableCondition: this.shipInfoEditableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.RECIPIENT_TEL
            }
          ],
          horizontal: true
        },
        {
          label: '〒',
          properties: [
            {
              path: 'address.postalCode', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'address', editableCondition: this.shipInfoEditableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.RECIPIENT_POSTAL_CODE
            }
          ]
        },
        {
          label: '都道府県',
          properties: [
            {
              path: 'address.stateCode', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'address', editableCondition: this.shipInfoEditableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.RECIPIENT_STATE
            }
          ]
        },
        {
          label: '市区町村',
          properties: [
            {
              path: 'address.city', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'address', editableCondition: this.shipInfoEditableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.RECIPIENT_CITY
            }
          ]
        },
        {
          label: '住所1',
          properties: [
            {
              path: 'address.address1', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'address', editableCondition: this.shipInfoEditableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.RECIPIENT_ADDRESS_1
            }
          ]
        },
        {
          label: '住所2',
          properties: [
            {
              path: 'address.address2', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'address', editableCondition: this.shipInfoEditableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.RECIPIENT_ADDRESS_2
            }
          ]
        },
        {
          label: '建物名・部屋番号',
          properties: [
            {
              path: 'address.suite', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'address', editableCondition: this.shipInfoEditableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.RECIPIENT_SUITE
            }
          ]
        }
      ]
    },
    shipmentInfo2 () {
      return [
        {
          label: '配送方法',
          properties: [
            {
              path: 'shippingMethod', type: 'String', elementType: ELEMENT_TYPE.SELECT_BOX,
              codeName: 'd_order_shipment.shipping_method', selectCode: 'd_order_shipment.shipping_method',
              editableId: 'shippingMethod', editableCondition: this.shipInfoEditableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.SHIPPING_METHOD
            }
          ]
        },
        {
          label: 'ギフトタイプ',
          properties: [
            {
              path: 'giftType', type: 'Number', elementType: ELEMENT_TYPE.SELECT_BOX,
              codeName: 'd_order_shipment.gift_type', selectCode: 'd_order_shipment.gift_type',
              editableId: 'giftType', editableCondition: this.shipInfoEditableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.GIFT_TYPE
            }
          ]
        },
        {
          label: 'ギフトメッセージ',
          properties: [
            {
              path: 'giftMessage', type: 'String', elementType: ELEMENT_TYPE.INPUT,
              editableId: 'giftMessage', editableCondition: this.shipInfoEditableCondition,
              validateRule: 'max:30', validateAs: 'ギフトメッセージ',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.GIFT_MESSAGE
            }
          ]
        },
        {
          label: '配達希望日',
          properties: [
            {
              path: 'deliveryDate', type: 'Date', elementType: ELEMENT_TYPE.DATE,
              editableId: 'deliveryDate', editableCondition: this.shipInfoEditableCondition,
              dateFormat: 'YYYY/MM/DD', dateOnly: true,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.DELIVERY_DATE
            }
          ]
        },
        {
          label: '配達希望時間帯',
          properties: [
            {
              path: 'deliveryTime', type: 'String', elementType: ELEMENT_TYPE.SELECT_BOX,
              codeName: 'd_order_shipment.delivery_time', selectCode: 'd_order_shipment.delivery_time', clearable: true,
              editableId: 'deliveryTime', editableCondition: this.shipInfoEditableCondition,
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.DELIVERY_TIME
            }
          ]
        },
        {
          label: '出荷予定日',
          properties: [
            {
              path: 'scheduledShippingAt', type: 'Date',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.SCHEDULED_SHIPPING_AT
            }
          ]
        },
        {
          label: '指図可能日時',
          properties: [
            {
              path: 'exportableAt', type: 'Date',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.EXPORTABLE_AT
            }
          ]
        },
        {
          label: 'リードタイム',
          properties: [
            {
              path: 'leadtime', type: 'String',
              funcId: define.PermissionKeys.Order.Detail.OrderInfo.ShipmentInfo.LEAD_TIME
            }
          ]
        }
      ]
    },
    emptyReceiverName () {
      return _.chain(this.shipment)
        .get('receiver')
        .thru(val => [_.get(val, 'lastName', ''), _.get(val, 'firstName', '')])
        .map(val => _.trim(val))
        .find(val => val !== '')
        .value() != null
    }
  },
  watch: {
    'shipment.giftType' (giftType) {
      if ([define.GIFT_TYPE.NONE, define.GIFT_TYPE.SUMMARY].includes(giftType)) {
        // ギフトタイプ.なし or ギフトタイプ.まとめ包装 の場合
        _.chain(this.shipment)
          .get('items')
          .forEach(val => {
            val.isGift = giftType === define.GIFT_TYPE.NONE ? 0 : 1
          })
          .value()
      }
    },
    'shipment.deliveryTime' (deliveryTime) {
      if (deliveryTime === undefined) {
        // 配達希望時間帯をクリアした場合、その項目が削除されたため、空文字で補足。
        this.shipment.deliveryTime = ''
      }
    },
    expandShipmentInfo: {
      handler: function (val) {
        const index = _.indexOf(this.activeCollapseName, 'shipmentInfo')
        if (val && index === -1) {
          this.activeCollapseName.push('shipmentInfo')
        } else if (!val && index !== -1) {
          this.activeCollapseName.splice(index, 1)
        }
      },
      deep: true
    },
    activeCollapseName (val) {
      this.$emit('update:expandShipmentInfo', _.indexOf(val, 'shipmentInfo') !== -1)
    }
  },
  created () {
    this.$validator = this.parentValidator

    if (this.type === 'normal') {
      // computed を渡すパターン
      this.$validator.extend(`checkReceiverName`, {
        getMessage: (field) => '姓、または名は必須です。',
        validate: (field, [ shipment ]) => {
          return {
            valid: shipment === 'true',
            data: {
              required: true
            }
          }
        }
      }, {
        computesRequired: true
      }
      )
    }

    if (this.expandShipmentInfo) {
      this.activeCollapseName.push('shipmentInfo')
    }
  },
  methods: {
    /**
     * 詳細な編集可否を判定する
     * @return {Boolean} 編集可否
     */
    shipInfoEditableCondition (row, path) {
      return _.chain(this.shipment)
        .get('items')
        .find(val => {
          return Number(_.get(val, 'qty.exported', 0)) !== 0 ||
            Number(_.get(val, 'qty.exOutExported', 0)) !== 0
        })
        .value() == null
    },
    openErrorField () {
      if (this.validateScope == null || this.validateScope === '' ||
          !this.errors.any(this.validateScope)) {
        return
      }
      if (this.validateScope != null && this.validateScope !== '' &&
          this.errors.any(this.validateScope) &&
          !_.find(this.activeCollapseName, val => val === 'shipmentInfo')) {
        this.activeCollapseName.push('shipmentInfo')
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/order/OrderDetailShipment.vue